/** One file, live: its seven-step trail, what it was matched to (with *Change*), the coverage cell it
 * will turn, and - once a person has accepted it - the result line with the figure, grade and ledger
 * entry.
 *
 * **Every claim here is read, none is assumed.** The trail is `trailOf` over the document row and its
 * review; the match is the uploader's site hint (a hint, said as one) and the values are the review's
 * own items; the cell is the census's, found only when exactly one expected source fits
 * (`coverageCell.ts`); the result line is the accepted document's usage, the activity's grade and the
 * receipt's ledger entry. A read that fails says so with a way to retry (`ReadFailed`), and none of
 * them is ever replaced by a zero or a blank that reads as a fact.
 *
 * **Reading is the person's act.** A checked document is *waiting*: the button calls the same
 * extraction the library's "Read and redact" does (the redaction gate is on it). Accepting is the
 * reviewer's, on the review screen, where the site and meter are chosen - which is where *Change*
 * goes. Nothing on this card accepts a figure.
 */

import { ArrowRight, FileText } from "@phosphor-icons/react";
import { useNavigate } from "@tanstack/react-router";

import { useDocument, useReview, useUsage } from "../../api/evidence";
import { useActivity, useReceipt } from "../../api/queries";
import { useCoverageRollup } from "../../api/coverageRollup";
import { useCoverage } from "../../api/sources";
import { ReadFailed } from "../../components/Loaded";
import { Skeleton, StatusChip } from "../../components/ui";
import type { ChipTone } from "../../components/ui";
import { EMISSIONS_UNIT } from "../../lib/emissionsUnit";
import { kindLabel } from "../../lib/documentKinds";
import { stageWord } from "../../lib/documentStages";
import { fileSize, shortDate, tonnes } from "../../lib/format";
import { placementOf } from "../coverage/waiting";
import { gradeLabel } from "../register";
import { StateSwatch } from "../SourcesHeatmap";
import { STATE } from "../sourceModel";
import type { Screen } from "../useScreen";
import { coverageCellFor } from "./coverageCell";
import { trailOf } from "./trail";
import { UploadTrail } from "./UploadTrail";
import styles from "./FileCard.module.css";

const TONE: Record<string, ChipTone> = {
  received: "neutral",
  validated: "info",
  extracting: "neutral",
  awaiting_review: "warn",
  verified: "ok",
  quarantined: "crit",
  rejected: "crit",
  superseded: "neutral",
};

/** The document states in which a file is on its way to a person and no figure exists from it. The
 * same four the server annotates a coverage window with (`evidence/stages.py` WAITING_STATES). */
const QUEUED: readonly string[] = ["received", "validated", "extracting", "awaiting_review"];

export interface FileCardProps {
  documentId: string;
  /** The name as the person chose it, shown while the server's row has not arrived. */
  fileName: string;
  /** The reporting window, for the coverage cell. `undefined` when none is in force. */
  window: { period_start: string; period_end: string } | undefined;
  /** Whether this person may read a document (the roles that may upload). */
  mayRead: boolean;
  reading: boolean;
  onRead: (documentId: string) => void;
  /** The search to carry to the review screen (the Scope 2 method). */
  method: Screen["method"];
}

export function FileCard({ documentId, fileName, window, mayRead, reading, onRead, method }: FileCardProps) {
  const navigate = useNavigate();
  const doc = useDocument(documentId);
  const row = doc.data ?? null;
  const reviewable = row?.state === "awaiting_review" || row?.state === "verified";
  const review = useReview(reviewable ? documentId : undefined);
  const usage = useUsage(documentId, row?.state === "verified");
  const first = usage.data?.activities[0];
  const activity = useActivity(first?.activity_record_id, first !== undefined);
  const calculationId = first?.calculations[0]?.calculation_id;
  const receipt = useReceipt(calculationId);
  const coverage = useCoverage(window?.period_start, window?.period_end, row !== null);
  //: **Where the server placed this file, if it did** (`GET /coverage`, ADR-0166). Asked only while
  //: the file is waiting for a person: once verified it is a figure, and the census says so itself.
  const queued = row !== null && QUEUED.includes(row.state) && mayRead && window !== undefined;
  //: The stamp makes the question be asked again when the file moves on: its stated period is read
  //: from the document after upload, and until then the server cannot place it on any window.
  const placed = useCoverageRollup(
    window?.period_start,
    window?.period_end,
    queued,
    `${row?.state ?? ""}|${row?.period_start ?? ""}|${row?.period_end ?? ""}`,
  );

  if (doc.isPending) {
    return (
      <div role="group" aria-label={`${fileName}, reading`} className={styles.card} aria-busy="true" data-testid="file-card" data-document-id={documentId}>
        {/* Shaped like the card it becomes: a title line, the trail's seven dots, one fact row. */}
        <Skeleton width="40%" height={16} />
        <Skeleton width="100%" height={22} />
        <Skeleton width="70%" height={12} />
        <span className="visuallyHidden" role="status">
          Reading the status of {fileName}
        </span>
      </div>
    );
  }
  if (doc.error || row === null) {
    return (
      <div role="group" aria-label={`${fileName}, unavailable`} className={styles.card} data-testid="file-card" data-document-id={documentId}>
        <ReadFailed
          of={`The status of ${fileName}`}
          error={doc.error ?? new Error("That document is not in this workspace.")}
          onRetry={() => void doc.refetch()}
        />
      </div>
    );
  }

  const trail = trailOf({ document: row, review: review.data ?? null });
  const found = review.data ? review.data.items.filter((item) => !item.missing).length : null;
  const total = review.data ? review.data.items.length : null;
  const lowOnes = review.data ? review.data.items.filter((item) => item.low_confidence && !item.missing).length : 0;

  const cell =
    coverage.data && row
      ? coverageCellFor({
          sources: coverage.data.sources,
          facilityId: first?.facility_id ?? row.facility_hint_id,
          category: first?.category,
          kind: row.kind,
          periodStart: row.period_start ?? first?.period_start,
          periodEnd: row.period_end ?? first?.period_end,
        })
      : null;

  const goReview = () =>
    void navigate({ to: "/evidence/$documentId", params: { documentId }, search: { method } });

  const figure = first?.calculations[0];
  const ledger = receipt.data?.ledger[0];

  return (
    <div role="group" aria-label={row.original_filename ?? fileName} className={styles.card} data-testid="file-card" data-document-id={documentId} data-state={row.state}>
      <header className={styles.head}>
        <FileText size={20} weight="duotone" aria-hidden="true" className={styles.fileIcon} />
        <div className={styles.titles}>
          <h3 className={styles.name}>{row.original_filename ?? fileName}</h3>
          <p className={styles.meta}>
            {kindLabel(row.kind)}
            <span aria-hidden="true"> · </span>
            {fileSize(row.byte_size)}
            {row.period_start && row.period_end ? (
              <>
                <span aria-hidden="true"> · </span>
                {shortDate(row.period_start)} – {shortDate(row.period_end)}
              </>
            ) : null}
          </p>
        </div>
        <StatusChip tone={TONE[row.state] ?? "neutral"} testId="file-state">
          {stageWord(row.stage)}
        </StatusChip>
      </header>

      <UploadTrail trail={trail} label={row.original_filename ?? fileName} />

      {row.state === "quarantined" ? (
        <p className={styles.refusal} role="alert" data-testid="file-refusal">
          {[row.state_reason, row.refusal_remedy].filter(Boolean).join(" ") || "This file was refused."}
        </p>
      ) : null}

      {row.state === "validated" ? (
        <div className={styles.actions}>
          {mayRead ? (
            <button
              type="button"
              className={styles.primary}
              disabled={reading}
              onClick={() => onRead(documentId)}
              data-testid="file-read"
            >
              {reading ? "Reading" : "Read it"}
            </button>
          ) : null}
        </div>
      ) : null}

      {reviewable ? (
        <div className={styles.match} data-testid="file-match">
        <dl className={styles.facts}>
          <div>
            <dt>Site</dt>
            <dd>
              {row.facility_hint_name ?? (
                <span className={styles.none}>chosen in review</span>
              )}
            </dd>
          </div>
          <div>
            <dt>Type</dt>
            <dd>{kindLabel(row.kind)}</dd>
          </div>
          <div>
            <dt>Period</dt>
            <dd>
              {row.period_start && row.period_end ? (
                `${shortDate(row.period_start)} – ${shortDate(row.period_end)}`
              ) : (
                <span className={styles.none}>not found</span>
              )}
            </dd>
          </div>
          <div>
            <dt>Values</dt>
            <dd>
              {review.error ? (
                <span className={styles.none}>could not be read</span>
              ) : found === null || total === null ? (
                <span className={styles.none}>reading</span>
              ) : (
                <>
                  {found} of {total}
                  {lowOnes > 0 ? <span className={styles.flag}> · {lowOnes} to check</span> : null}
                </>
              )}
            </dd>
          </div>
        </dl>
          {row.state === "awaiting_review" ? (
            <div className={styles.matchAction}>
              <button type="button" className={styles.ghost} onClick={goReview} data-testid="file-change">
                Change
              </button>
              <button type="button" className={styles.primary} onClick={goReview} data-testid="file-review">
                Open review <ArrowRight size={14} aria-hidden="true" />
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {review.error && reviewable ? (
        <ReadFailed of="The values read from this file" error={review.error} onRetry={() => void review.refetch()} />
      ) : null}

      {cell ? (
        <div className={styles.cell} data-testid="file-cell" data-state={cell.window.state}>
          <span className={styles.cellLabel}>
            {cell.source.facility_name}, {shortDate(cell.window.period_start)} – {shortDate(cell.window.period_end)}
          </span>
          {/* Keyed on the state so a change of state replays the one-shot turn (CSS, transform only). */}
          <span key={cell.window.state} className={styles.turn}>
            <StateSwatch state={cell.window.state}>{STATE[cell.window.state]?.short}</StateSwatch>
          </span>
        </div>
      ) : null}

      {queued ? <QueuedLine read={placed} documentId={documentId} /> : null}

      {row.state === "verified" ? (
        usage.error || activity.error || receipt.error ? (
          <ReadFailed
            of="The result of this file"
            error={usage.error ?? activity.error ?? receipt.error}
            onRetry={() => {
              void usage.refetch();
              void activity.refetch();
              void receipt.refetch();
            }}
          />
        ) : (
          <p className={styles.result} data-testid="file-result">
            {figure && receipt.data ? (
              <>
                <strong className="num">
                  {tonnes(receipt.data.figure_kgco2e)} {EMISSIONS_UNIT}
                </strong>
                {activity.data ? <span>{gradeLabel(activity.data.quality)}</span> : null}
                {ledger ? <span className="num">Ledger #{ledger.seq}</span> : null}
                <button
                  type="button"
                  className={styles.link}
                  onClick={() =>
                    void navigate({
                      to: "/emissions",
                      search: { method, receipt: figure.calculation_id },
                    })
                  }
                >
                  Open receipt
                </button>
              </>
            ) : usage.isPending || receipt.isPending ? (
              <span className={styles.none}>Reading the result</span>
            ) : (
              <span className={styles.none}>No figure came from this file</span>
            )}
          </p>
        )
      ) : null}
    </div>
  );
}

/** One line: this file is in the review queue for a window, and that does not clear the window.
 *
 * Read from the server's annotation (`placementOf`), so it appears only when the server placed the
 * file on a window from the uploader's own site and the kind they chose. **The window keeps its
 * census state**, said in the census's word; "received", "in" and a check mark are not used because
 * each would read as the window being accounted for. Honest in three states: still asking, could not
 * read (with a retry), and read with the file not placed - which says nothing, because a file the
 * server could not place is not a file the card may place.
 */
function QueuedLine({
  read,
  documentId,
}: {
  read: ReturnType<typeof useCoverageRollup>;
  documentId: string;
}) {
  if (read.error) {
    return (
      <ReadFailed
        of="Where this file sits in coverage"
        error={read.error}
        onRetry={() => void read.refetch()}
      />
    );
  }
  if (read.isPending) {
    return (
      <p className={styles.queuedPending} role="status" data-testid="file-queued-pending">
        Checking where this file sits in coverage
      </p>
    );
  }
  const where = placementOf(read.data, documentId);
  if (!where) return null;
  return (
    <p className={styles.queued} data-testid="file-queued" data-state={where.state}>
      In the review queue for {where.facilityName}, {shortDate(where.periodStart)} &ndash;{" "}
      {shortDate(where.periodEnd)}. That window stays {STATE[where.state]?.short ?? where.state} until a
      person verifies this file, so it does not clear the gap yet.
    </p>
  );
}
